import React, {useEffect, useRef, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {DownloadOutlined} from '@ant-design/icons';
import AsyncReportDownloadModal, {dashboardAsyncReportLabelI18nKeys} from '../../../components/asyncReportDownload';
import {
    GENERATE_APPEAL_REVIEW_REPORT,
    GenerateAppealReviewReportResponse,
    GenerateAppealReviewReportVariables,
} from './graphql/mutations/generateAppealReviewReport';
import {Button, DatePicker, Modal, Popconfirm, Space, Table, Tabs, message} from 'antd';
import type {TableColumnsType} from 'antd';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import ParcelFilterStyle from '../../delivery/components/ParcelFilterStyle';
import BatchInputFilterItem from '../../delivery/components/BatchInputFilterItem';
import {splitTrackingNumbers} from '../../../components/parcelQueryRange/trackingNumbers';
import {WarehouseSelectFilter} from '../../../components/filters/WarehouseSelectFilter';
import {PickupHubSelectFilter} from '../../../components/filters/PickupHubSelectFilter';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {useDspOptions} from '../../../hooks/useDspOptions';
import {PenaltySource} from '../penaltyCenter/graphql/types';
import {
    CreationTimeRange,
    disableBeyondMaxDays,
    getDefaultCreationTimeRange,
    toCreationTimeRangeInput,
} from './utils/creationTimeRange';
import {
    AppealTicketFiltersTypeInput,
    AppealTicketStatus,
    AppealTicketType,
    GET_APPEAL_TICKET_COUNT,
    GET_APPEAL_TICKET_PAGE_LIST,
    GetAppealTicketCountResponse,
    GetAppealTicketPageListResponse,
} from './graphql/queries/getAppealTicketPageList';
import {
    REVIEW_APPEAL_TICKET,
    ReviewAppealTicketAction,
    ReviewAppealTicketResponse,
} from './graphql/mutations/reviewAppealTicket';
import {VOID_APPEAL_TICKETS, VoidAppealTicketsResponse} from './graphql/mutations/voidAppealTickets';
import PenaltyDetailDrawer from './components/PenaltyDetailDrawer';
import RejectReasonModal from './components/RejectReasonModal';
import {
    useFilterContainerClassName,
    useFilterInputContainerClassName,
    useFilterLabelClassName,
} from '../../../utils/languageUtils';
import {getSmartLabelClassName} from '../../../utils/labelWidthUtils';

interface AppealFilters {
    hubId?: string;
    warehouseId?: string;
    dspId?: number;
    /** 输入框原文，查询时拆分为 trackingNumbers 数组 */
    trackingNumber?: string;
    creationTimeRange?: CreationTimeRange;
    penaltySources?: PenaltySource[];
}

const TAB_KEYS: AppealTicketStatus[] = [
    AppealTicketStatus.PENDING,
    AppealTicketStatus.WAITING,
    AppealTicketStatus.APPROVED,
    AppealTicketStatus.REJECTED,
    AppealTicketStatus.TIMEOUT,
    AppealTicketStatus.PENDING_SUBMIT,
];

const TAB_LABEL_KEY: Record<AppealTicketStatus, string> = {
    [AppealTicketStatus.PENDING]: 'partnerControl.appealReview.tab.pending',
    [AppealTicketStatus.WAITING]: 'partnerControl.appealReview.tab.waiting',
    [AppealTicketStatus.APPROVED]: 'partnerControl.appealReview.tab.approved',
    [AppealTicketStatus.REJECTED]: 'partnerControl.appealReview.tab.rejected',
    [AppealTicketStatus.TIMEOUT]: 'partnerControl.appealReview.tab.timeout',
    [AppealTicketStatus.PENDING_SUBMIT]: 'partnerControl.appealReview.tab.pending_submit',
};

/** 可执行审核操作（操作列、多选、批量审核）的 Tab */
const REVIEWABLE_TABS: AppealTicketStatus[] = [AppealTicketStatus.PENDING, AppealTicketStatus.WAITING];

/** DSP 尚未正式提交申诉的 Tab，不展示申诉理由、申诉时间列 */
const NO_APPEAL_TABS: AppealTicketStatus[] = [AppealTicketStatus.TIMEOUT, AppealTicketStatus.PENDING_SUBMIT];

const REVIEW_SUCCESS_KEY: Record<ReviewAppealTicketAction, string> = {
    [ReviewAppealTicketAction.APPROVE]: 'partnerControl.appealReview.action.approveSuccess',
    [ReviewAppealTicketAction.REJECT]: 'partnerControl.appealReview.action.rejectSuccess',
    [ReviewAppealTicketAction.WAITING]: 'partnerControl.appealReview.action.waitingSuccess',
};

/** 单次查询/导出的运单号上限，与后端 APPEAL_MAX_TRACKING_NUMBERS 一致 */
const MAX_TRACKING_NUMBERS = 200;

const PAGE_SIZE_OPTIONS = ['10', '20', '50', '100', '200'];

const getDefaultFilters = (): AppealFilters => ({creationTimeRange: getDefaultCreationTimeRange()});

/**
 * 非当前 Tab 用独立计数查询；当前 Tab 复用列表 totalElements，省一次请求。
 *
 * `skip: true→false`（这个 Tab 从当前变为非当前）时 Apollo 会自动发起首次查询，不用管；
 * 但如果这个 Tab 本来就不是当前 Tab（`skip` 一直是 `false`），切到第三个 Tab 时，对
 * *这个 hook 自己* 来说 `skip` 这个值根本没变化过（一直是 `false`），Apollo 的
 * `useQuery` 只在 `skip` 这个具体布尔值变化时才会自动重新发起请求，不会重新查——
 * 因此不能只监听自己的 `skip`，必须监听 `activeTab` 这个外部信号本身：每次 Tab 切换都
 * 检查一遍"这次切换前后 skip 是否真的变了"，没变但还是非当前（false→false）时手动 refetch，
 * 保证每次切换 Tab 后，两个非当前 Tab 的计数都是最新的，同时避免和 Apollo 自动触发的那次重复请求。
 */
const useTabCount = (status: AppealTicketStatus, filters: AppealTicketFiltersTypeInput, activeTab: AppealTicketStatus) => {
    const skip = activeTab === status;
    const {data, loading, refetch} = useQuery<GetAppealTicketCountResponse>(GET_APPEAL_TICKET_COUNT, {
        variables: {filters: {...filters, status}},
        skip,
        fetchPolicy: 'network-only',
    });

    const prevSkipRef = useRef(skip);
    const isFirstRenderRef = useRef(true);
    useEffect(() => {
        const prevSkip = prevSkipRef.current;
        prevSkipRef.current = skip;
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        if (prevSkip === skip && !skip) {
            void refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    return {count: data?.getAppealTicketCount, loading, refetch};
};

const AppealReviewPage: React.FC = () => {
    const {t, i18n} = useTranslation();
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [downloadFileName, setDownloadFileName] = useState('');
    const labelHub = String(t('partnerControl.appealReview.filter.hub'));
    const labelWarehouse = String(t('partnerControl.appealReview.filter.warehouse'));
    const labelDsp = String(t('partnerControl.appealReview.filter.dsp'));
    const labelTrackingNumber = String(t('partnerControl.appealReview.filter.trackingNumber'));
    const labelCreationTime = String(t('partnerControl.appealReview.filter.creationTime'));
    const labelPenaltySource = String(t('partnerControl.appealReview.filter.penaltySource'));

    const [activeTab, setActiveTab] = useState<AppealTicketStatus>(AppealTicketStatus.PENDING);
    const [tempFilters, setTempFilters] = useState<AppealFilters>(getDefaultFilters());
    const [filters, setFilters] = useState<AppealFilters>(getDefaultFilters());
    // DSP 独立筛选：不依赖所选仓库，可在全部 DSP 中单选
    const {dspOptions: allDspOptions, loading: dspLoading} = useDspOptions();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [reviewTicketIds, setReviewTicketIds] = useState<number[]>([]);
    const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<AppealTicketType[]>([]);
    const [rejectModalState, setRejectModalState] = useState<{ open: boolean; ticketId: number | null }>({
        open: false,
        ticketId: null,
    });
    const [rejecting, setRejecting] = useState(false);
    // 已选仓库所属 Hub（parentSiteId），用于选 Hub 时判断已选仓库是否需要清空
    const selectedWarehouseHubIdRef = useRef<string | undefined>(undefined);

    // 批量审核：reviewTicketIds 为空 = 抽屉关闭；长度为 1 时等价于单条详情
    const currentTicketId = reviewTicketIds[currentReviewIndex] ?? null;
    const isBatchReviewMode = reviewTicketIds.length > 1;

    const openReview = (ids: number[]) => {
        setReviewTicketIds(ids);
        setCurrentReviewIndex(0);
    };

    const closeReviewDrawer = () => {
        setReviewTicketIds([]);
        setCurrentReviewIndex(0);
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };

    // 单条场景下 nextIndex(1) >= length(1) 直接落到 close 分支，行为与改动前一致；
    // 行内按钮审核时 reviewTicketIds 本就是空数组，这里清空一次空状态，无副作用
    const moveToNext = () => {
        const nextIndex = currentReviewIndex + 1;
        if (nextIndex < reviewTicketIds.length) {
            setCurrentReviewIndex(nextIndex);
        } else {
            closeReviewDrawer();
        }
    };

    const containerClassName = useFilterContainerClassName();
    // 筛选区所有标签共用最长标签的宽度，三列输入框起点上下对齐
    const filterLabelWidthClassName = getSmartLabelClassName([
        labelTrackingNumber, labelHub, labelWarehouse, labelDsp, labelCreationTime, labelPenaltySource,
    ]);
    const labelClassName = useFilterLabelClassName(filterLabelWidthClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    const dspOptions = (allDspOptions ?? []).map((dsp) => ({value: Number(dsp.value), label: dsp.label}));

    // 罚款原因类型是后端固定 5 枚举，选项文案复用罚单管理中心的同名 key
    const penaltySourceOptions = Object.values(PenaltySource).map((code) => ({
        value: code,
        label: String(t(`partnerControl.penaltyCenter.penaltySource.${code}`, {defaultValue: code})),
    }));

    const trackingNumbers = splitTrackingNumbers(filters.trackingNumber);
    const filtersInput: AppealTicketFiltersTypeInput = {
        hubId: filters.hubId,
        warehouseId: filters.warehouseId,
        dspId: filters.dspId,
        trackingNumbers: trackingNumbers.length ? trackingNumbers : undefined,
        creationTimeRange: toCreationTimeRangeInput(filters.creationTimeRange),
        penaltySources: filters.penaltySources?.length ? filters.penaltySources : undefined,
    };

    const {data, loading, refetch} = useQuery<GetAppealTicketPageListResponse>(GET_APPEAL_TICKET_PAGE_LIST, {
        variables: {
            filters: {...filtersInput, status: activeTab},
            page: currentPage,
            size: pageSize,
        },
        fetchPolicy: 'network-only',
    });

    const listing = data?.getAppealTicketPageList;
    const tableData = listing?.content ?? [];

    // 当前 Tab 的计数复用列表 totalElements；非当前 Tab 各自发一次轻量计数请求。
    // 三个 Tab 固定，逐个显式调用 useTabCount，不放进循环/map —— 避免违反 Hooks 调用顺序规则。
    const pendingCount = useTabCount(AppealTicketStatus.PENDING, filtersInput, activeTab);
    const waitingCount = useTabCount(AppealTicketStatus.WAITING, filtersInput, activeTab);
    const approvedCount = useTabCount(AppealTicketStatus.APPROVED, filtersInput, activeTab);
    const rejectedCount = useTabCount(AppealTicketStatus.REJECTED, filtersInput, activeTab);
    const timeoutCount = useTabCount(AppealTicketStatus.TIMEOUT, filtersInput, activeTab);
    const pendingSubmitCount = useTabCount(AppealTicketStatus.PENDING_SUBMIT, filtersInput, activeTab);

    const tabCountByStatus: Record<AppealTicketStatus, {count?: number; loading: boolean}> = {
        [AppealTicketStatus.PENDING]:
            activeTab === AppealTicketStatus.PENDING ? {count: listing?.totalElements, loading} : pendingCount,
        [AppealTicketStatus.WAITING]:
            activeTab === AppealTicketStatus.WAITING ? {count: listing?.totalElements, loading} : waitingCount,
        [AppealTicketStatus.APPROVED]:
            activeTab === AppealTicketStatus.APPROVED ? {count: listing?.totalElements, loading} : approvedCount,
        [AppealTicketStatus.REJECTED]:
            activeTab === AppealTicketStatus.REJECTED ? {count: listing?.totalElements, loading} : rejectedCount,
        [AppealTicketStatus.TIMEOUT]:
            activeTab === AppealTicketStatus.TIMEOUT ? {count: listing?.totalElements, loading} : timeoutCount,
        [AppealTicketStatus.PENDING_SUBMIT]:
            activeTab === AppealTicketStatus.PENDING_SUBMIT
                ? {count: listing?.totalElements, loading}
                : pendingSubmitCount,
    };

    const tabs = TAB_KEYS.map((status) => {
        const tabCount = tabCountByStatus[status];
        const countText = tabCount.loading || tabCount.count == null ? '...' : tabCount.count;
        return {
            key: status,
            label: `${t(TAB_LABEL_KEY[status])} (${countText})`,
        };
    });

    const [reviewAppealTicket] = useMutation<ReviewAppealTicketResponse>(REVIEW_APPEAL_TICKET);
    const [voidAppealTickets, {loading: voiding}] = useMutation<VoidAppealTicketsResponse>(VOID_APPEAL_TICKETS);
    const [generateReport, { loading: reportGenerating }] = useMutation<
        GenerateAppealReviewReportResponse,
        GenerateAppealReviewReportVariables
    >(GENERATE_APPEAL_REVIEW_REPORT);

    const handleExport = async () => {
        const language = i18n.language.split('-')[0];
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        try {
            const res = await generateReport({
                variables: {
                    hubId: filters.hubId ? Number(filters.hubId) : null,
                    warehouseId: filters.warehouseId ? Number(filters.warehouseId) : null,
                    dspId: filters.dspId ?? null,
                    trackingNumbers: filtersInput.trackingNumbers ?? null,
                    creationTimeRange: filtersInput.creationTimeRange ?? null,
                    penaltySources: filtersInput.penaltySources ?? null,
                    language,
                    timezone,
                },
            });
            const result = res.data?.generateAppealReviewReport;
            if (result?.success) {
                setDownloadFileName(result.fileName ?? '');
                setDownloadModalVisible(true);
            } else if (result?.errorCode === 'NO_DATA') {
                message.warning(t('partnerControl.appealReview.export.noData'));
            } else if (result?.errorCode?.startsWith('EXCEED_MAX_LIMIT')) {
                const max = result.errorCode.split(':')[1] ?? '';
                message.error(t('partnerControl.appealReview.export.exceedMaxLimit', { max }));
            } else {
                message.error(t('partnerControl.appealReview.export.failed'));
            }
        } catch {
            message.error(t('partnerControl.appealReview.export.failed'));
        }
    };

    /**
     * 审核动作会让该罚单从当前 Tab 移到另一个 Tab，当前列表 + 另外两个 Tab 的计数都要刷新。
     * 当前 Tab 自己的计数查询是 skip 状态（复用列表 totalElements），不能对它调用 refetch。
     */
    const refreshAfterReview = () => {
        void refetch();
        // 审核会让罚单在「待处理/待确认/已通过/已驳回」之间流转，「已超时」计数不受影响，无需刷新；
        // 按状态显式配对，不依赖 TAB_KEYS 下标顺序
        ([
            [AppealTicketStatus.PENDING, pendingCount],
            [AppealTicketStatus.WAITING, waitingCount],
            [AppealTicketStatus.APPROVED, approvedCount],
            [AppealTicketStatus.REJECTED, rejectedCount],
        ] as const)
            .filter(([status]) => status !== activeTab)
            .forEach(([, tabCount]) => void tabCount.refetch());
    };

    // 作废只影响「待DSP提交」（当前 Tab），其计数复用列表 totalElements，刷新列表即可
    const handleVoid = () => {
        const ids = selectedRows.map((row) => row.id);
        Modal.confirm({
            content: t('partnerControl.appealReview.void.confirmContent', {count: ids.length}),
            okText: t('common.confirm', 'OK'),
            cancelText: t('common.cancel', 'Cancel'),
            okButtonProps: {danger: true},
            onOk: async () => {
                try {
                    const res = await voidAppealTickets({variables: {ids}});
                    const {voidedCount = 0, skippedCount = 0} = res.data?.voidAppealTickets ?? {};
                    if (skippedCount > 0) {
                        message.warning(
                            t('partnerControl.appealReview.void.partial', {voided: voidedCount, skipped: skippedCount})
                        );
                    } else {
                        message.success(t('partnerControl.appealReview.void.success', {count: voidedCount}));
                    }
                } catch {
                    message.error(t('partnerControl.appealReview.void.failed'));
                }
                setSelectedRowKeys([]);
                setSelectedRows([]);
                void refetch();
            },
        });
    };

    // Hub ↔ 仓库联动：选 Hub 时清掉不属于该 Hub 的已选仓库；清空 Hub 保留仓库
    const handleHubChange = (hubId?: string) => {
        setTempFilters((prev) => ({
            ...prev,
            hubId,
            warehouseId: hubId && selectedWarehouseHubIdRef.current !== hubId ? undefined : prev.warehouseId,
        }));
    };

    // 选仓库时 Hub 自动填为其 parentSiteId（为空则不变）；清空仓库保留 Hub
    const handleWarehouseParentSiteId = (parentSiteId?: string) => {
        selectedWarehouseHubIdRef.current = parentSiteId;
        if (parentSiteId) {
            setTempFilters((prev) => ({...prev, hubId: parentSiteId}));
        }
    };

    const handleSearch = () => {
        const count = splitTrackingNumbers(tempFilters.trackingNumber).length;
        if (count > MAX_TRACKING_NUMBERS) {
            message.error(
                t('partnerControl.appealReview.filter.trackingNumberExceedLimit', {count, max: MAX_TRACKING_NUMBERS})
            );
            return;
        }
        setFilters(tempFilters);
        setCurrentPage(1);
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };

    const handleReset = () => {
        const defaults = getDefaultFilters();
        setTempFilters(defaults);
        setFilters(defaults);
        setCurrentPage(1);
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };

    const handleReview = async (id: number, action: ReviewAppealTicketAction, rejectReason?: string) => {
        await reviewAppealTicket({variables: {id, action, rejectReason}});
        message.success(t(REVIEW_SUCCESS_KEY[action]));
        refreshAfterReview();
        moveToNext();
    };

    const openRejectModal = (ticketId: number) => {
        setRejectModalState({ open: true, ticketId });
    };

    const handleRejectConfirm = async (reason: string) => {
        if (rejectModalState.ticketId == null) return;
        setRejecting(true);
        try {
            await handleReview(rejectModalState.ticketId, ReviewAppealTicketAction.REJECT, reason);
            setRejectModalState({ open: false, ticketId: null });
        } finally {
            setRejecting(false);
        }
    };

    const columns: TableColumnsType<AppealTicketType> = [
        {
            title: t('partnerControl.appealReview.table.ticketNo'),
            dataIndex: 'ticketNo',
            key: 'ticketNo',
            ellipsis: true,
            render: (value: string, record) => (
                <Button
                    type="link"
                    size="small"
                    className="!px-0"
                    onClick={() => openReview([record.id])}
                >
                    {value}
                </Button>
            ),
        },
        {
            title: t('partnerControl.appealReview.table.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            ellipsis: true,
            render: (value?: string | null) => value ?? '--',
        },
        {
            title: t('partnerControl.appealReview.table.dspName'),
            dataIndex: 'dspName',
            key: 'dspName',
            ellipsis: true,
            render: (value?: string | null) => value ?? '--',
        },
        {
            title: t('partnerControl.appealReview.table.reason'),
            dataIndex: 'reason',
            key: 'reason',
            ellipsis: true,
            render: (value?: string | null) => value ?? '--',
        },
        {
            title: t('partnerControl.appealReview.table.amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (value?: number | null) => (value != null ? `$${value.toLocaleString()}` : '--'),
        },
        ...(NO_APPEAL_TABS.includes(activeTab)
            ? []
            : [
                  {
                      title: t('partnerControl.appealReview.table.appealReason'),
                      dataIndex: 'appealReason',
                      key: 'appealReason',
                      render: (value?: string | null) => {
                          if (!value) return '--';
                          return value.length > 12 ? `${value.slice(0, 12)}...` : value;
                      },
                  },
                  {
                      title: t('partnerControl.appealReview.table.appealTime'),
                      dataIndex: 'appealTime',
                      key: 'appealTime',
                      ellipsis: true,
                      // appealTime 是 api-server 透传的 ISO-8601 字符串，dayjs 可直接解析
                      render: (value?: string | null) =>
                          value != null ? dayjs(value).format('YYYY-MM-DD HH:mm') : '--',
                  },
              ]),
        ...(REVIEWABLE_TABS.includes(activeTab)
            ? [
                  {
                      title: t('partnerControl.appealReview.table.action'),
                      key: 'action',
                      fixed: 'right' as const,
                      width: 280,
                      render: (_: unknown, record: AppealTicketType) => (
                          <div className="flex gap-2">
                              <Popconfirm
                                  title={t('partnerControl.appealReview.action.approveConfirmTitle')}
                                  onConfirm={() => handleReview(record.id, ReviewAppealTicketAction.APPROVE)}
                                  okText={t('common.confirm', 'OK')}
                                  cancelText={t('common.cancel', 'Cancel')}
                              >
                                  <Button size="small" type="primary" className="!bg-green-600 hover:!bg-green-500">
                                      {t('partnerControl.appealReview.action.approve')}
                                  </Button>
                              </Popconfirm>
                              {/* 待确认只能从「待处理」进入，待确认 Tab 不再显示该按钮 */}
                              {activeTab === AppealTicketStatus.PENDING && (
                                  <Popconfirm
                                      title={t('partnerControl.appealReview.action.waitingConfirmTitle')}
                                      onConfirm={() => handleReview(record.id, ReviewAppealTicketAction.WAITING)}
                                      okText={t('common.confirm', 'OK')}
                                      cancelText={t('common.cancel', 'Cancel')}
                                  >
                                      <Button size="small">{t('partnerControl.appealReview.action.waiting')}</Button>
                                  </Popconfirm>
                              )}
                              <Button size="small" danger onClick={() => openRejectModal(record.id)}>
                                  {t('partnerControl.appealReview.action.reject')}
                              </Button>
                          </div>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <div className="p-4">
            <div className="mb-4 text-lg font-bold">{t('partnerControl.appealReview.title')}</div>

            <div className="mb-2 py-2">
                <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                    <div className="min-w-0 col-span-3">
                        <BatchInputFilterItem
                            label={labelTrackingNumber}
                            value={tempFilters.trackingNumber}
                            onChange={(e) =>
                                setTempFilters({...tempFilters, trackingNumber: e.target.value || undefined})
                            }
                            placeholder={t('partnerControl.appealReview.filter.trackingNumberPlaceholder') as string}
                            spanClassName={filterLabelWidthClassName}
                            rows={3}
                        />
                    </div>
                    <div className="min-w-0">
                        <PickupHubSelectFilter
                            label={labelHub}
                            value={tempFilters.hubId}
                            onChange={handleHubChange}
                            placeholder={t('partnerControl.appealReview.filter.hubPlaceholder') as string}
                            spanClassName={filterLabelWidthClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <WarehouseSelectFilter
                            label={labelWarehouse}
                            value={tempFilters.warehouseId}
                            hubId={tempFilters.hubId}
                            onChange={(value) => setTempFilters((prev) => ({...prev, warehouseId: value}))}
                            onSelectParentSiteId={handleWarehouseParentSiteId}
                            placeholder={t('partnerControl.appealReview.filter.warehousePlaceholder') as string}
                            spanClassName={filterLabelWidthClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={labelDsp}
                            options={dspOptions}
                            value={tempFilters.dspId != null ? String(tempFilters.dspId) : undefined}
                            onChange={(value) =>
                                setTempFilters({...tempFilters, dspId: value ? Number(value) : undefined})
                            }
                            loading={dspLoading}
                            placeholder={t('partnerControl.appealReview.filter.dspPlaceholder') as string}
                            spanClassName={filterLabelWidthClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <div className={containerClassName}>
                            <span className={labelClassName}>{labelCreationTime}</span>
                            <div className={inputContainerClassName}>
                                <DatePicker.RangePicker
                                    className="w-full"
                                    value={tempFilters.creationTimeRange}
                                    disabledDate={disableBeyondMaxDays}
                                    onChange={(dates) =>
                                        setTempFilters({
                                            ...tempFilters,
                                            creationTimeRange:
                                                dates?.[0] && dates?.[1] ? [dates[0], dates[1]] : undefined,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={labelPenaltySource}
                            mode="multiple"
                            maxTagCount="responsive"
                            options={penaltySourceOptions}
                            value={tempFilters.penaltySources ?? []}
                            onChange={(value: PenaltySource[]) =>
                                setTempFilters({...tempFilters, penaltySources: value.length ? value : undefined})
                            }
                            placeholder={t('partnerControl.appealReview.filter.penaltySourcePlaceholder') as string}
                            spanClassName={filterLabelWidthClassName}
                        />
                    </div>
                </div>
                <div className="flex justify-between items-center mt-3">
                    <Button
                        icon={<DownloadOutlined />}
                        loading={reportGenerating}
                        onClick={handleExport}
                    >
                        {t('partnerControl.appealReview.export.button')}
                    </Button>
                    <Space>
                        <Button onClick={handleReset} className="w-auto min-w-24">
                            <span className="font-bold">{t('partnerControl.appealReview.actions.reset')}</span>
                        </Button>
                        <Button type="primary" onClick={handleSearch} className="w-auto min-w-24">
                            <span className="font-bold">{t('partnerControl.appealReview.actions.search')}</span>
                        </Button>
                    </Space>
                </div>
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                    setActiveTab(key as AppealTicketStatus);
                    setCurrentPage(1);
                    setSelectedRowKeys([]);
                    setSelectedRows([]);
                }}
                items={tabs}
                className="[&_.ant-tabs-nav]:!mb-3 [&_.ant-tabs-tab]:font-bold"
            />

            {activeTab === AppealTicketStatus.PENDING_SUBMIT && (
                <div className="flex justify-end mb-3">
                    <Button danger disabled={selectedRows.length === 0} loading={voiding} onClick={handleVoid}>
                        {t('partnerControl.appealReview.void.button')} ({selectedRows.length})
                    </Button>
                </div>
            )}

            {REVIEWABLE_TABS.includes(activeTab) && (
                <div className="flex justify-end mb-3">
                    <Button
                        type="primary"
                        disabled={selectedRows.length === 0}
                        onClick={() => openReview(selectedRows.map((row) => row.id))}
                    >
                        {t('partnerControl.appealReview.action.batchReview')} ({selectedRows.length})
                    </Button>
                </div>
            )}

            <div className="mb-4">
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={tableData}
                    loading={loading}
                    pagination={false}
                    scroll={{x: 'max-content'}}
                    rowSelection={
                        REVIEWABLE_TABS.includes(activeTab) || activeTab === AppealTicketStatus.PENDING_SUBMIT
                            ? {
                                  selectedRowKeys,
                                  onChange: (keys, rows) => {
                                      setSelectedRowKeys(keys);
                                      setSelectedRows(rows);
                                  },
                              }
                            : undefined
                    }
                />
            </div>

            <TablePaginationBar
                total={listing?.totalElements ?? 0}
                current={currentPage}
                pageSize={pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                    setSelectedRowKeys([]);
                    setSelectedRows([]);
                    void refetch();
                }}
            />

            <PenaltyDetailDrawer
                ticketId={currentTicketId}
                onClose={closeReviewDrawer}
                onApprove={(id) => void handleReview(id, ReviewAppealTicketAction.APPROVE)}
                onReject={(id) => openRejectModal(id)}
                onWaiting={(id) => void handleReview(id, ReviewAppealTicketAction.WAITING)}
                isBatchMode={isBatchReviewMode}
                currentIndex={currentReviewIndex}
                total={reviewTicketIds.length}
                onSkip={moveToNext}
            />

            <AsyncReportDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={downloadFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.appealReviewExport}
            />

            <RejectReasonModal
                open={rejectModalState.open}
                loading={rejecting}
                onConfirm={(reason) => void handleRejectConfirm(reason)}
                onCancel={() => setRejectModalState({ open: false, ticketId: null })}
            />
        </div>
    );
};

export default AppealReviewPage;
